import React, { useEffect, useState } from "react";

import type { UseMutateFunction } from "@tanstack/react-query";
import InfoIcon from "@uui/InfoCircle";
import ShareIcon from "@uui/Link01";
import TranscriptIcon from "@uui/MessageTextSquare02";
import ThumbsDownIcon from "@uui/ThumbsDown";
import ThumbsUpIcon from "@uui/ThumbsUp";
import CloseIcon from "@uui/XClose";
import { isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import type { SxProps } from "@mui/material";
import Typography from "@mui/material/Typography";

import useAudioControls from "@app/analytics/components/DailyReadPlayer/useAudioControls";
import PlayerControls from "@app/analytics/components/PlayerControls";
import useDailyRead from "@app/analytics/hooks/recordings/useDailyRead";
import useDailyReadAudio from "@app/analytics/hooks/recordings/useDailyReadAudio";
import useDailyReadContext from "@app/analytics/hooks/useDailyReadContext";
import useDailyReadStore from "@app/analytics/stores/useDailyReadStore";
import * as Events from "@app/analytics/tracking/events";
import type {
  DailyReadFeedback,
  DailyReadMetadata,
  VideoPlayerAction,
} from "@app/analytics/types";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import { ELLIPSIS_SX } from "@app/common/styles";
import Config from "@app/config";
import { getAppUrl } from "@app/utils/utils";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import CopyButton from "@rui/CopyButton";
import IconBackground from "@rui/IconBackground";
import Stack, { Row } from "@rui/Stack";

import {
  AcknowledgeButton,
  AddReadButton,
  BetaBadge,
  Container,
  DisclaimerContainer,
  FeedbackButton,
  MinimizedPlayerButton,
  PlayerButton,
  PlayerContainer,
  PromptContainer,
  TranscriptContainer,
} from "./styles";

interface DailyReadPlayerProps {
  dismissible?: boolean;
  displayOnly?: boolean;
  /* if passed, will use this value instead of the value coming off the metadata to determine if the data is "stale" */
  isStale?: boolean;
  forCurrentUser?: boolean;
  metadata: DailyReadMetadata;
  minimizable?: boolean;
  onMaximize?: () => void;
  onMinimize?: () => void;
  onPlay?: () => void;
  onPlayed?: () => void;
  onSubmitFeedback?: UseMutateFunction<
    void,
    Error,
    { id: string; feedback: DailyReadFeedback }
  >;
  shareable?: boolean;
  sx?: SxProps;
  withTranscript?: boolean;
}

const EMPTY_TRANSCRIPT: string[] = [];

const getCopyContent = (id: string) => getAppUrl(ROUTES.dailyReadById(id));

const MinimizedPlayer = ({ onClick }: { onClick: () => void }) => (
  <MinimizedPlayerButton onClick={onClick}>🎧 Daily Read</MinimizedPlayerButton>
);

const DailyReadPlayer = ({
  displayOnly = false,
  forCurrentUser = false,
  isStale,
  metadata,
  minimizable = false,
  onMaximize,
  onMinimize,
  onPlay,
  onPlayed,
  onSubmitFeedback,
  shareable = false,
  sx,
  withTranscript = false,
}: DailyReadPlayerProps) => {
  const store = useDailyReadStore();
  const { isLoading, url, fetchAudio } = useDailyReadAudio(metadata.id);

  const controls = useAudioControls({
    id: metadata.id,
    src: url,
    displayOnly,
    onPlayed,
  });

  const [showTranscript, setShowTranscript] = useState(false);
  const [pendingFeedback, setPendingFeedback] =
    useState<DailyReadFeedback | null>(null);
  const [ttsDisclaimerAcknowledged, setTtsDisclaimerAcknowledged] =
    useLocalStorage("ttsDisclaimerAcknowledged", false);

  const shouldAutoPlay = Boolean(url) && store.autoPlayOnLoad;

  useEffect(() => {
    if (!shouldAutoPlay) return;
    // once the url populates, start playing
    controls.play();
    store.setAutoPlayOnLoad(false);
  }, [shouldAutoPlay]);

  const handleMaximize = () => {
    store.maximize();
    onMaximize?.();
  };

  if (minimizable && store.minimized) {
    return <MinimizedPlayer onClick={handleMaximize} />;
  }

  const handleToggleTranscript = () => {
    if (!showTranscript) {
      Events.openDailyReadTranscript();
    }
    setShowTranscript((show) => !show);
  };

  const transcript = metadata.transcript ?? EMPTY_TRANSCRIPT;
  const hasTranscript = withTranscript && !isEmpty(transcript);

  const handleAcknowledgeTTSDisclaimer = () => {
    Events.acknowledgeTTSDisclaimer();
    setTtsDisclaimerAcknowledged(true);
  };

  const handleDismiss = () => {
    store.minimize();
    setShowTranscript(false);
    controls.pause();
    // reset back to the start
    controls.onScrubberChange(0);
    onMinimize?.();
  };

  const handleActionClick = (type: VideoPlayerAction) => {
    switch (type) {
      case "play":
        if (url) {
          onPlay?.();
          return controls.play();
        }
        // when clicking play for the first time, fetch the audio and autoplay
        // once it's been fetched
        store.setAutoPlayOnLoad(true);
        return fetchAudio();
      case "pause":
        return controls.pause();
      case "skipBackward10":
        return controls.skipBackward();
      case "skipForward10":
        return controls.skipForward();
      case "mute":
        return controls.mute();
      case "unmute":
        return controls.unmute();
      default:
        return;
    }
  };

  const handleSubmitFeedback = (feedback: DailyReadFeedback) => () => {
    if (!onSubmitFeedback) return;
    setPendingFeedback(feedback);
    onSubmitFeedback(
      { id: metadata.id, feedback },
      {
        onError: () => {
          toast.error(
            "Something went wrong and your feedback could not be saved. Please refresh the page or try again later."
          );
          setPendingFeedback(null);
        },
        onSuccess: () => {
          setTimeout(() => setPendingFeedback(null), 3000);
        },
      }
    );
  };

  const showFeedback =
    forCurrentUser &&
    Boolean(onSubmitFeedback) &&
    store.promptFeedback &&
    (metadata.feedback == null || pendingFeedback != null);

  const metadataIsStale = Boolean(isStale ?? metadata.isStale);

  return (
    <Container sx={sx}>
      <PlayerContainer>
        <Row justifyContent="space-between" spacing={2}>
          <Stack sx={{ overflow: "hidden" }}>
            <Row spacing={1} variant="left">
              <Typography variant="h3" fontWeight={500} sx={ELLIPSIS_SX}>
                {!forCurrentUser && metadata.userName
                  ? `${metadata.userName}'s `
                  : ""}
                Daily Read
              </Typography>
              {Config.flags.dailyReadIsBeta && <BetaBadge>✨ beta</BetaBadge>}
            </Row>
            <Typography paddingTop={0.5}>
              {metadata.date.toLocaleString(DateTime.DATE_FULL)}
            </Typography>
          </Stack>
          <Stack>
            <Row variant="right" spacing="6px">
              {hasTranscript && (
                <PlayerButton
                  active={showTranscript}
                  Icon={TranscriptIcon}
                  onClick={handleToggleTranscript}
                  tooltip={
                    showTranscript ? "Hide transcript" : "View transcript"
                  }
                />
              )}
              {shareable && (
                <CopyButton
                  copyValue={getCopyContent(metadata.id)}
                  onClick={() => Events.shareDailyRead()}
                  renderIconButton={({ onClick }, copied) => (
                    <PlayerButton
                      copied={copied}
                      Icon={ShareIcon}
                      tooltip={copied ? "Copied!" : "Share link"}
                      onClick={onClick}
                    />
                  )}
                />
              )}
              {minimizable && (
                <PlayerButton
                  Icon={CloseIcon}
                  onClick={handleDismiss}
                  tooltip="Close"
                />
              )}
            </Row>
          </Stack>
        </Row>
        <Stack paddingTop={1}>
          <PlayerControls
            additionalActions={[
              { action: "skipBackward10" },
              { action: "skipForward10" },
            ]}
            currentTime={url ? store.currentTimestamp : undefined}
            duration={url ? store.duration : undefined}
            loadingAction={isLoading ? "play" : undefined}
            muted={store.muted}
            onActionClick={handleActionClick}
            onScrubberChange={controls.onScrubberChange}
            paused={store.paused}
          />
        </Stack>
        <AutoAnimateContainer show={!ttsDisclaimerAcknowledged}>
          <DisclaimerContainer spacing={1.5} variant="apart">
            <Row variant="left" spacing={0.5}>
              <IconBackground height={24}>
                <InfoIcon sx={{ color: "shades.secondary.200" }} />
              </IconBackground>
              <Typography variant="labelReg">
                The text-to-speech voice you are hearing is AI-generated and not
                a human voice.
              </Typography>
            </Row>
            <AcknowledgeButton onClick={handleAcknowledgeTTSDisclaimer}>
              Got it
            </AcknowledgeButton>
          </DisclaimerContainer>
        </AutoAnimateContainer>
      </PlayerContainer>
      <AutoAnimateContainer show={showFeedback}>
        <PromptContainer variant="apart">
          {metadata.feedback == null ? (
            <>
              <Typography variant="bodySecondaryReg">
                Was this Daily Read accurate?
              </Typography>
              <Row variant="right" spacing={1}>
                <FeedbackButton
                  onClick={handleSubmitFeedback("positive")}
                  loading={pendingFeedback === "positive"}
                >
                  <ThumbsUpIcon />
                </FeedbackButton>
                <FeedbackButton
                  onClick={handleSubmitFeedback("negative")}
                  loading={pendingFeedback === "negative"}
                >
                  <ThumbsDownIcon />
                </FeedbackButton>
              </Row>
            </>
          ) : (
            <Typography variant="bodySecondaryReg">
              Thanks for your feedback 🙂
            </Typography>
          )}
        </PromptContainer>
      </AutoAnimateContainer>
      <AutoAnimateContainer show={metadataIsStale}>
        <PromptContainer variant="apart" spacing={1}>
          <Typography variant="labelReg">
            No new Daily Read. Add Read to more meetings to generate a new Daily
            Read every day.
          </Typography>
          <AddReadButton />
        </PromptContainer>
      </AutoAnimateContainer>
      {hasTranscript && (
        <AutoAnimateContainer
          show={showTranscript}
          sx={{ maxHeight: "325px", overflow: "hidden" }}
        >
          <TranscriptContainer spacing={1.5}>
            {transcript.map((paragraph) => (
              <Typography variant="bodySecondaryReg" key={paragraph}>
                {paragraph}
              </Typography>
            ))}
          </TranscriptContainer>
        </AutoAnimateContainer>
      )}
    </Container>
  );
};

const ConnectedDailyReadPlayer = (props: DailyReadPlayerProps) => {
  const { submitFeedback } = useDailyRead();
  const { updateDailyReadState } = useDailyReadContext();

  if (props.displayOnly || !props.forCurrentUser) {
    return <DailyReadPlayer {...props} />;
  }

  const handleMaximize = () => {
    updateDailyReadState({ lastMinimizedId: null });
  };

  const handleMinimize = () => {
    updateDailyReadState({ lastMinimizedId: props.metadata.id });
  };

  const handlePlay = () => {
    updateDailyReadState({
      lastPlayedId: props.metadata.id,
    });
  };

  const handlePlayed = () => {
    updateDailyReadState({ lastListenedId: props.metadata.id });
  };

  return (
    <DailyReadPlayer
      {...props}
      onPlay={handlePlay}
      onPlayed={handlePlayed}
      onMaximize={handleMaximize}
      onMinimize={handleMinimize}
      onSubmitFeedback={submitFeedback.mutate}
    />
  );
};

export { DailyReadPlayer };
export default ConnectedDailyReadPlayer;
